import type { HTMLAttributes, ReactNode } from "react";

import { useI18n } from "../use-i18n";
import { cn } from "../utils";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcHeading, type NcHeadingProps } from "./nc-heading";
import { NcLoadingIndicator } from "./nc-loading-indicator";

export interface NcPanelProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const NcPanel = ({ children, className, ...props }: NcPanelProps) => {
  return (
    <div
      data-nc="NcPanel"
      className={cn(
        "border-ui bg-ui-light flex flex-col content-start overflow-y-auto rounded-md border print:max-h-none",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

interface NcPanelHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const NcPanelHeader = ({ children, className, ...props }: NcPanelHeaderProps) => {
  return (
    <div
      data-nc="NcPanel.Header"
      className={cn("flex shrink-0 basis-6 items-center rounded-t-md p-3", className)}
      {...props}
    >
      {children}
    </div>
  );
};
NcPanel.Header = NcPanelHeader;

const NcPanelHeading = ({ children, ...props }: Omit<NcHeadingProps, "styleAs">) => (
  <NcHeading styleAs={4} {...props}>
    {children}
  </NcHeading>
);
NcPanel.Heading = NcPanelHeading;

interface NcPanelBodyProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  error?: boolean | string;
  empty?: boolean | string;
  isLoading?: boolean;
}

type NcPanelBodyState = "LOADING" | "LOADED" | "EMPTY" | "ERROR";

function getPanelState({
  isLoading,
  empty,
  error,
}: Omit<NcPanelBodyProps, "children">): NcPanelBodyState {
  if (isLoading) return "LOADING";
  if (error) return "ERROR"; // Error must come before empty as error state may also have empty data
  if (empty) return "EMPTY";
  return "LOADED";
}

const panelBodyClasses =
  "bg-light border-ui flex grow flex-col overflow-y-auto px-3 py-3 not-only:border-t print:overflow-visible print:max-h-none";

const NcPanelBody = ({
  children,
  isLoading,
  empty,
  error,
  className,
  ...props
}: NcPanelBodyProps) => {
  const state = getPanelState({ isLoading, empty, error });
  if (state === "LOADED") {
    return (
      <div data-nc="NcPanel.Body" className={cn(panelBodyClasses, className)} {...props}>
        {children}
      </div>
    );
  }
  return (
    <div data-nc="NcPanel.Body" className={cn(panelBodyClasses)}>
      {state === "LOADING" && (
        <div className="text-brand flex w-full grow items-center justify-center">
          <NcLoadingIndicator />
        </div>
      )}
      {state === "ERROR" && <NcPanelBodyError error={error} />}
      {state === "EMPTY" && <NcPanelBodyEmpty empty={empty} />}
    </div>
  );
};
NcPanel.Body = NcPanelBody;

const NcPanelBodyError = ({ error }: { error: NcPanelBodyProps["error"] }) => {
  const { t } = useI18n();
  const errorText = typeof error === "string" ? error : t("error_default");
  return <NcFormattedMessage variant="danger">{errorText}</NcFormattedMessage>;
};

const NcPanelBodyEmpty = ({ empty }: { empty: NcPanelBodyProps["empty"] }) => {
  const { t } = useI18n();
  const emptyText = typeof empty === "string" ? empty : t("empty_default");
  return <NcFormattedMessage variant="secondary">{emptyText}</NcFormattedMessage>;
};

interface NcPanelFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const NcPanelFooter = ({ children, className, ...props }: NcPanelFooterProps) => {
  return (
    <div
      data-nc="NcPanel.Footer"
      className={cn(
        "bg-light border-ui flex min-h-12 shrink-0 items-center rounded-b-md border-t px-3 py-3",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

NcPanel.Footer = NcPanelFooter;
